// dNews / Newsletter
//
// Admin-only compose surface. Modeled on the CreateNotification "Notification For"
// pattern (general / department / job_position / location / employee) but with a
// rich-text body, file attachments, and a Send Now / Send Later split.
//
// From-address is hardcoded to connect@dolluzcorp.com (replies route to the
// Dolluz mailbox). v2 will add the connect@/noreply@ dropdown per the freeze
// doc - intentionally deferred to keep v1 lean.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import Select from "react-select";
import { checkboxMultiProps } from "./utils/MultiSelectCheckbox";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import {
    FaPaperPlane, FaClock, FaPaperclip, FaTrashAlt, FaUsers,
} from "react-icons/fa";
import { apiFetch } from "./utils/api";
import DNewsSelect from "./utils/dNews_Select";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./dNews_Newsletter.css";
const DEFAULT_FROM = "connect@dolluzcorp.com";

// Audience selector matches CreateNotification.js exactly: a single dropdown
// drives whether (and what) the second selector renders. "general" needs no
// second selector - it just resolves to all employees on the server.
const TARGET_OPTIONS = [
    { value: "general",       label: "General (All Employees)" },
    { value: "department",    label: "Department" },
    { value: "job_position",  label: "Job Position" },
    { value: "location",      label: "Location" },
    { value: "employee",      label: "Specific Employees" },
    { value: "contact",       label: "Contact (non-employee)" },
    { value: "contact_group", label: "Distribution Group" },
];

export default function DNewsNewsletter({ navSize }) {
    const navigate = useNavigate();

    // ── Form state ───────────────────────────────────────────────────────────
    const [senders, setSenders] = useState([]);          // [{ email, name, label }]
    const [fromEmail, setFromEmail] = useState(DEFAULT_FROM);
    const [targetType, setTargetType] = useState("");   // no default - admin must choose
    const [targetIds,  setTargetIds]  = useState([]);
    const [subject,    setSubject]    = useState("");
    const [files,      setFiles]      = useState([]);  // local File[] before upload
    const [sending,    setSending]    = useState(false);
    const [scheduleMode, setScheduleMode] = useState(false);
    const [scheduledAt,  setScheduledAt]  = useState("");

    // ── Cc / Bcc state - collapsed by default, opened via "+ Cc / Bcc" toggle.
    //    Each uses the same target_type + ids shape as the primary To row.
    //    "" target_type means the row is intentionally empty (no extras to send).
    // Label categorises the send; picking one gates the per-label unsubscribe link.
    const [labels, setLabels] = useState([]);
    const [labelId, setLabelId] = useState(null);
    const [includeUnsub, setIncludeUnsub] = useState(false);
    const [showCcBcc, setShowCcBcc] = useState(false);
    const [ccType,  setCcType]  = useState("");
    const [ccIds,   setCcIds]   = useState([]);
    const [bccType, setBccType] = useState("");
    const [bccIds,  setBccIds]  = useState([]);

    // ── Audience-options + live recipient count ──────────────────────────────
    const [audience,    setAudience]    = useState(null);   // { departments, jobPositions, locations, employees }
    const [recipientCount, setRecipientCount] = useState(0);

    // ── Quill rich-text body (same toolbar as Dolluzcorp Updates) ────────────
    const { quill, quillRef } = useQuill({
        theme: "snow",
        modules: {
            toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                [{ color: [] }, { background: [] }],
                ["link"],
                ["clean"],
            ],
        },
    });

    // ── Auth check + audience pre-fetch ──────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/api/dnews/newsletter/audience-options", { credentials: "include" });
                if (res.status === 401 || res.status === 403) { navigate("/login"); return; }
                const data = await res.json();
                setAudience(data);
                const lblRes = await apiFetch("/api/dnews/labels/list", { credentials: "include" });
                const llist = await lblRes.json().catch(() => []);
                setLabels(Array.isArray(llist) ? llist : []);
            } catch {
                navigate("/login");
            }
        })();
    }, [navigate]);

    // ── Senders list for the From dropdown ───────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/api/dnews/senders/allowed", { credentials: "include" });
                if (!res.ok) return;
                const data = await res.json();
                setSenders(Array.isArray(data) ? data : []);
            } catch { /* leave default */ }
        })();
    }, []);

    // ── Recipient preview: re-resolve on every audience-selection change ─────
    useEffect(() => {
        const abort = new AbortController();
        (async () => {
            try {
                const res = await apiFetch("/api/dnews/newsletter/resolve-recipients", {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ target_type: targetType, ids: targetIds }),
                    signal: abort.signal,
                });
                if (!res.ok) { setRecipientCount(0); return; }
                const data = await res.json();
                setRecipientCount(data.count || 0);
            } catch { /* aborted or network blip - leave previous count */ }
        })();
        return () => abort.abort();
    }, [targetType, targetIds]);

    // ── Options + label helper for the second selector ──────────────────────
    // Guards against undefined arrays in case the API response is partial -
    // react-select calls .filter on this value and crashes if it's not an array.
    const secondaryOptions = (() => {
        if (!audience) return [];
        switch (targetType) {
            case "department":    return audience.departments  || [];
            case "job_position":  return audience.jobPositions || [];
            case "location":      return audience.locations    || [];
            case "employee":      return audience.employees    || [];
            case "contact":       return audience.contacts     || [];
            case "contact_group": return audience.groups       || [];
            default:              return [];
        }
    })();

    const onTargetTypeChange = (v) => {
        setTargetType(v);
        setTargetIds([]);
    };

    // Build the secondary multi-select options for ANY target_type (used
    // by both Cc and Bcc). Keeps the lookup logic out of the JSX below.
    const optionsFor = (t) => {
        if (!audience) return [];
        switch (t) {
            case "department":    return audience.departments  || [];
            case "job_position":  return audience.jobPositions || [];
            case "location":      return audience.locations    || [];
            case "employee":      return audience.employees    || [];
            case "contact":       return audience.contacts     || [];
            case "contact_group": return audience.groups       || [];
            default:              return [];
        }
    };

    // "general" needs no second selector. The empty string means "this Cc/Bcc
    // is unused" - lets the user open the row without committing to a target.
    const TARGET_OPTIONS_OPTIONAL = [
        { value: "",              label: "- None -" },
        ...TARGET_OPTIONS,
    ];

    // ── Attachment add/remove (kept on local state, sent as multipart on submit)
    const onPickFiles = (e) => {
        const picked = Array.from(e.target.files || []);
        const over = picked.find(f => f.size > 5 * 1024 * 1024);
        if (over) {
            dnAlert.warning("File too large", `"${over.name}" exceeds the 5MB per-file limit.`);
            e.target.value = "";
            return;
        }
        // At most 3 files, 15MB total (matches the backend + SendGrid's cap).
        if (files.length + picked.length > 3) {
            dnAlert.warning("Too many files", "You can attach at most 3 files.");
            e.target.value = "";
            return;
        }
        const total = [...files, ...picked].reduce((s, f) => s + f.size, 0);
        if (total > 15 * 1024 * 1024) {
            dnAlert.warning("Attachments too large", "Total attachment size must stay under 15MB.");
            e.target.value = "";
            return;
        }
        setFiles(prev => [...prev, ...picked]);
        e.target.value = ""; // allow re-picking the same file
    };
    const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

    // ── Submit ───────────────────────────────────────────────────────────────
    const validate = () => {
        if (!subject.trim()) return "Subject is required.";
        const bodyHtml = quill?.root?.innerHTML?.trim() || "";
        if (!bodyHtml || bodyHtml === "<p><br></p>") return "Body cannot be empty.";
        if (!targetType) return "Choose who to send to in the To field.";
        if (targetType !== "general" && targetIds.length === 0) {
            return `Pick at least one ${targetType.replace("_", " ")}.`;
        }
        if (recipientCount === 0) return "No recipients resolved for the selected audience.";
        if (scheduleMode) {
            if (!scheduledAt) return "Pick a date/time to schedule.";
            if (new Date(scheduledAt).getTime() < Date.now()) return "Scheduled time must be in the future.";
        }
        return null;
    };

    const onSubmit = async () => {
        const err = validate();
        if (err) {
            dnAlert.warning("Cannot send", err);
            return;
        }
        const action = scheduleMode ? "Schedule" : "Send";
        const ok = await dnAlert.confirm({
            title: `${action} this newsletter?`,
            html: `<p><strong>Subject:</strong> ${escapeHtml(subject)}</p>
                   <p><strong>Recipients:</strong> ${recipientCount} ${recipientCount === 1 ? "person" : "people"}</p>
                   <p><strong>From:</strong> ${fromEmail}</p>
                   ${scheduleMode ? `<p><strong>Scheduled for:</strong> ${new Date(scheduledAt).toLocaleString()}</p>` : `<p><strong>When:</strong> Immediately</p>`}`,
            confirmLabel: scheduleMode ? "Schedule" : "Send now",
        });
        if (!ok) return;

        const fd = new FormData();
        fd.append("target_type", targetType);
        fd.append("target_ids", JSON.stringify(targetIds));
        // Cc / Bcc only included when the row is open AND a type was picked
        // (empty string means "— None —" = ignore). Backend treats missing
        // fields as no extras.
        if (showCcBcc && ccType) {
            fd.append("cc_target_type", ccType);
            fd.append("cc_target_ids", JSON.stringify(ccIds));
        }
        if (showCcBcc && bccType) {
            fd.append("bcc_target_type", bccType);
            fd.append("bcc_target_ids", JSON.stringify(bccIds));
        }
        fd.append("from_email", fromEmail);
        fd.append("subject", subject);
        fd.append("body_html", quill.root.innerHTML);
        fd.append("include_unsubscribe", includeUnsub ? "1" : "0");
        if (labelId) fd.append("label_id", String(labelId));
        if (scheduleMode) fd.append("scheduled_at", new Date(scheduledAt).toISOString());
        for (const f of files) fd.append("attachments", f);

        setSending(true);
        try {
            const endpoint = scheduleMode
                ? "/api/dnews/newsletter/schedule"
                : "/api/dnews/newsletter/send";
            const res = await apiFetch(endpoint, { method: "POST", credentials: "include", body: fd });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Send failed");

            if (scheduleMode) {
                dnAlert.success("Scheduled", `Newsletter scheduled for ${new Date(data.scheduled_for).toLocaleString()} (${data.recipients} recipients).`);
            } else {
                const failedNote = data.failed > 0 ? ` ${data.failed} failed.` : "";
                const summary = `${data.sent} of ${data.sent + data.failed} delivered.${failedNote}`;
                if (data.failed === 0) dnAlert.success("Sent", summary);
                else                   dnAlert.warning("Sent with errors", summary);
            }

            // Reset for the next compose. Also reset audience so the form
            // returns to a true blank-slate state - was previously leaving
            // the same recipients ticked after a successful send, which led
            // to accidental re-sends.
            setSubject("");
            setFiles([]);
            if (quill) quill.setText("");
            setScheduleMode(false);
            setScheduledAt("");
            setTargetType("general");
            setTargetIds([]);
            setShowCcBcc(false);
            setCcType(""); setCcIds([]);
            setBccType(""); setBccIds([]);
            setIncludeUnsub(false);
            setLabelId(null);
        } catch (e) {
            dnAlert.error("Send failed", e.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="dspr-container">
            <div className={`dspr-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#"><img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} /></a>
                        dNews - Newsletter
                    </h2>
                    <p className="dspr-subtitle">Compose and send emails to employees - now or scheduled. Pick the audience by department, job position, location, or specific people.</p>
                </div>

                <div className="dspr-dashboard-content">
                <section className="dnews-card">
                    {/* From - whitelisted senders pulled from backend */}
                    <div className="dnews-row">
                        <label className="dnews-label dnews-label-req">From</label>
                        <DNewsSelect
                            value={fromEmail}
                            onChange={(v) => setFromEmail(v)}
                            options={(senders.length ? senders : [{ email: DEFAULT_FROM, label: DEFAULT_FROM }])
                                .map(s => ({ value: s.email, label: s.label || s.email }))}
                        />
                    </div>

                    {/* To */}
                    <div className="dnews-row">
                        <label className="dnews-label dnews-label-req">To</label>
                        <DNewsSelect
                            value={targetType}
                            onChange={(v) => onTargetTypeChange(v)}
                            options={TARGET_OPTIONS}
                            placeholder="- Choose recipients -"
                        />
                    </div>

                    {/* Secondary selector - only when not general */}
                    {targetType !== "general" && (
                        <div className="dnews-row">
                            <label className="dnews-label">Select {targetType.replace("_", " ")}</label>
                            <Select
                                key={targetType}
                                isMulti
                                {...checkboxMultiProps}
                                isLoading={!audience}
                                options={secondaryOptions}
                                value={secondaryOptions.filter(o => targetIds.includes(String(o.value)))}
                                onChange={(sel) => setTargetIds(sel ? sel.map(s => String(s.value)) : [])}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                placeholder={`Pick one or more ${targetType.replace("_", " ")}...`}
                            />
                        </div>
                    )}

                    {/* ── Cc / Bcc - collapsed by default. Each row mirrors the
                         To picker shape (DNewsSelect for type + react-select for
                         ids). Setting type to "— None —" effectively turns the
                         row off. */}
                    {!showCcBcc ? (
                        <button
                            type="button"
                            className="dnews-ccbcc-toggle"
                            onClick={() => setShowCcBcc(true)}
                        >
                            + Add Cc / Bcc
                        </button>
                    ) : (
                        <div className="dnews-ccbcc-block">
                            {/* Cc */}
                            <div className="dnews-row">
                                <label className="dnews-label">Cc (optional)</label>
                                <DNewsSelect
                                    value={ccType}
                                    onChange={(v) => { setCcType(v); setCcIds([]); }}
                                    options={TARGET_OPTIONS_OPTIONAL}
                                />
                            </div>
                            {ccType && ccType !== "general" && (
                                <div className="dnews-row">
                                    <label className="dnews-label">Cc - Select {ccType.replace("_", " ")}</label>
                                    <Select
                                        key={`cc-${ccType}`}
                                        isMulti
                                        {...checkboxMultiProps}
                                        isLoading={!audience}
                                        options={optionsFor(ccType)}
                                        value={optionsFor(ccType).filter(o => ccIds.includes(String(o.value)))}
                                        onChange={(sel) => setCcIds(sel ? sel.map(s => String(s.value)) : [])}
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        placeholder={`Pick one or more ${ccType.replace("_", " ")}...`}
                                    />
                                </div>
                            )}

                            {/* Bcc */}
                            <div className="dnews-row">
                                <label className="dnews-label">Bcc (optional)</label>
                                <DNewsSelect
                                    value={bccType}
                                    onChange={(v) => { setBccType(v); setBccIds([]); }}
                                    options={TARGET_OPTIONS_OPTIONAL}
                                />
                            </div>
                            {bccType && bccType !== "general" && (
                                <div className="dnews-row">
                                    <label className="dnews-label">Bcc - Select {bccType.replace("_", " ")}</label>
                                    <Select
                                        key={`bcc-${bccType}`}
                                        isMulti
                                        {...checkboxMultiProps}
                                        isLoading={!audience}
                                        options={optionsFor(bccType)}
                                        value={optionsFor(bccType).filter(o => bccIds.includes(String(o.value)))}
                                        onChange={(sel) => setBccIds(sel ? sel.map(s => String(s.value)) : [])}
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        placeholder={`Pick one or more ${bccType.replace("_", " ")}...`}
                                    />
                                </div>
                            )}

                            <button
                                type="button"
                                className="dnews-ccbcc-hide"
                                onClick={() => {
                                    setShowCcBcc(false);
                                    setCcType("");  setCcIds([]);
                                    setBccType(""); setBccIds([]);
                                }}
                            >
                                − Hide Cc / Bcc
                            </button>
                        </div>
                    )}

                    {/* Live recipient count */}
                    <div className="dnews-recipient-count">
                        <FaUsers /> ≈{recipientCount} {recipientCount === 1 ? "recipient" : "recipients"}
                    </div>

                    {/* Subject */}
                    <div className="dnews-row">
                        <label className="dnews-label dnews-label-req">Subject</label>
                        <input
                            type="text"
                            className="dnews-input"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Email subject line"
                            maxLength={250}
                        />
                    </div>

                    {/* Body - Quill */}
                    <div className="dnews-row">
                        <label className="dnews-label dnews-label-req">Body</label>
                        <div className="dnews-quill-host">
                            <div ref={quillRef} />
                        </div>
                    </div>

                    {/* Attachments */}
                    <div className="dnews-row">
                        <label className="dnews-label">Attachments</label>
                        <div className="dnews-attach-zone">
                            <label className="dnews-attach-btn">
                                <FaPaperclip /> Add files
                                <input type="file" multiple onChange={onPickFiles} hidden />
                            </label>
                            <span className="dnews-attach-hint">Max 5MB per file · up to 3 files</span>
                        </div>
                        {files.length > 0 && (
                            <ul className="dnews-attach-list">
                                {files.map((f, i) => (
                                    <li key={i}>
                                        <FaPaperclip className="dnews-attach-icon" />
                                        <span className="dnews-attach-name">{f.name}</span>
                                        <span className="dnews-attach-size">{(f.size / 1024).toFixed(1)} KB</span>
                                        <button type="button" className="dnews-attach-remove" onClick={() => removeFile(i)}>
                                            <FaTrashAlt />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Label - categorises the send; picking one auto-enables the
                        unsubscribe link (recipients opt out of THIS label only). */}
                    <div className="dnews-row">
                        <label className="dnews-label">Label (optional)</label>
                        <DNewsSelect
                            value={labelId ?? ""}
                            onChange={(v) => {
                                const id = v ? Number(v) : null;
                                setLabelId(id);
                                setIncludeUnsub(!!id);
                            }}
                            options={[{ value: "", label: "No label" },
                                      ...labels.filter(l => l.active !== 0).map(l => ({ value: l.id, label: l.name }))]}
                            placeholder="Pick a label…"
                        />
                    </div>

                    {/* Unsubscribe link - only once a label is chosen; the footer opts
                        the person out of that label's mail (not all dNews). */}
                    <div className="dnews-schedule-row">
                        <label className="dnews-schedule-check" style={!labelId ? { opacity: .5 } : undefined}>
                            <input
                                type="checkbox"
                                checked={includeUnsub}
                                disabled={!labelId}
                                onChange={(e) => setIncludeUnsub(e.target.checked)}
                            />
                            <FaUsers /> Include an “Unsubscribe” link in this email
                            {!labelId && <span style={{ color: "#94a3b8", marginLeft: 6 }}>(pick a label first)</span>}
                        </label>
                    </div>

                    {/* Schedule toggle - small inline checkbox so there's no
                        confusion between a mode-toggle button and the actual
                        send action. The bottom button is the only CTA. */}
                    <div className="dnews-schedule-row">
                        <label className="dnews-schedule-check">
                            <input
                                type="checkbox"
                                checked={scheduleMode}
                                onChange={(e) => setScheduleMode(e.target.checked)}
                            />
                            <FaClock /> Schedule for later
                        </label>
                        {scheduleMode && (
                            <input
                                type="datetime-local"
                                className="dnews-input dnews-schedule-input"
                                value={scheduledAt}
                                onChange={(e) => setScheduledAt(e.target.value)}
                            />
                        )}
                    </div>

                    {/* Single submit CTA */}
                    <div className="dnews-submit-row">
                        <button
                            type="button"
                            className="dnews-submit-btn"
                            disabled={sending}
                            onClick={onSubmit}
                        >
                            {sending
                                ? <><span className="dn-spinner" /> Sending…</>
                                : scheduleMode
                                    ? <><FaClock /> Schedule</>
                                    : <><FaPaperPlane /> Send now</>}
                        </button>
                    </div>
                </section>
                </div>
            </div>
        </div>
    );
}

// Tiny HTML-escape so the confirmation modal can show subject verbatim
// without rendering markup the user typed.
function escapeHtml(s) {
    return String(s || "")
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
